import type { EChartsOption } from '../echarts-types.ts';
import type { ChartConfig } from '../types.ts';
import { seriesPalette } from '../theme.ts';
import {
  bodyTooltip,
  chartTitle,
  multiSeriesLegend,
  cartesianGrid,
  type OptionContext,
} from './shared.ts';

export function buildBarOption(
  config: Extract<ChartConfig, { type: 'bar' }>,
  { theme, base }: OptionContext
): EChartsOption {
  const { colors, textColor, axisLineColor } = theme;
  const max = Math.max(...config.values, 1);

  return {
    ...base,
    title: chartTitle(config.title, textColor),
    tooltip: bodyTooltip({ trigger: 'axis', axisPointer: { type: 'shadow' } }),
    grid: {
      left: 8,
      right: 24,
      top: config.title ? 36 : 8,
      bottom: 8,
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      max,
      axisLabel: { color: textColor, fontSize: 10 },
      axisLine: { lineStyle: { color: axisLineColor } },
      splitLine: { lineStyle: { color: axisLineColor } },
    },
    yAxis: {
      type: 'category',
      data: config.labels,
      inverse: true,
      axisLabel: { color: textColor, fontSize: 11 },
      axisLine: { lineStyle: { color: axisLineColor } },
    },
    series: [
      {
        type: 'bar',
        data: config.values,
        barMaxWidth: 16,
        itemStyle: { color: colors.primary, borderRadius: [0, 4, 4, 0] },
        label: {
          show: true,
          position: 'right',
          color: textColor,
          fontSize: 10,
          formatter: '{c}',
        },
      },
    ],
  };
}

export function buildColumnOption(
  config: Extract<ChartConfig, { type: 'column' }>,
  { theme, base }: OptionContext
): EChartsOption {
  const { colors, textColor, axisLineColor } = theme;
  const max = Math.max(...config.values, 1);
  return {
    ...base,
    title: chartTitle(config.title, textColor),
    tooltip: bodyTooltip({ trigger: 'axis', axisPointer: { type: 'shadow' } }),
    grid: {
      left: 8,
      right: 12,
      top: config.title ? 36 : 8,
      bottom: 8,
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: config.labels,
      axisLabel: { color: textColor, fontSize: 10 },
      axisLine: { lineStyle: { color: axisLineColor } },
    },
    yAxis: {
      type: 'value',
      max,
      axisLabel: { color: textColor, fontSize: 10 },
      splitLine: { lineStyle: { color: axisLineColor } },
    },
    series: [
      {
        type: 'bar',
        data: config.values,
        barMaxWidth: 22,
        itemStyle: { color: colors.primary, borderRadius: [4, 4, 0, 0] },
        label: {
          show: true,
          position: 'top',
          color: textColor,
          fontSize: 10,
          formatter: '{c}',
        },
      },
    ],
  };
}

export function buildGroupedColumnOption(
  config: Extract<ChartConfig, { type: 'grouped_column' }>,
  { theme, base }: OptionContext
): EChartsOption {
  const { colors, textColor, axisLineColor } = theme;
  const yMax = Math.max(
    1,
    ...config.labels.map((_, i) =>
      config.series.reduce((max, ser) => Math.max(max, ser.values[i] ?? 0), 0)
    )
  );
  const palette = seriesPalette(colors);
  return {
    ...base,
    title: chartTitle(config.title, textColor),
    tooltip: bodyTooltip({ trigger: 'axis', axisPointer: { type: 'shadow' } }),
    legend: multiSeriesLegend(config.series.length, textColor),
    grid: cartesianGrid(config.title, config.series.length),
    xAxis: {
      type: 'category',
      data: config.labels,
      axisLabel: { color: textColor, fontSize: 10 },
      axisLine: { lineStyle: { color: axisLineColor } },
    },
    yAxis: {
      type: 'value',
      max: yMax,
      axisLabel: { color: textColor, fontSize: 10 },
      splitLine: { lineStyle: { color: axisLineColor } },
    },
    series: config.series.map((s, si) => ({
      name: s.name,
      type: 'bar' as const,
      data: s.values,
      barMaxWidth: 22,
      barGap: '10%',
      itemStyle: {
        color: palette[si % palette.length],
        borderRadius: [4, 4, 0, 0],
      },
    })),
  };
}

export function buildStackedBarOption(
  config: Extract<ChartConfig, { type: 'stacked_bar' }>,
  { theme, base }: OptionContext
): EChartsOption {
  const { colors, textColor, axisLineColor } = theme;
  const stackMax = Math.max(
    1,
    ...config.labels.map((_, i) =>
      config.series.reduce((sum, ser) => sum + (ser.values[i] ?? 0), 0)
    )
  );
  const totals = config.totals;
  const totalsMax = totals && totals.length > 0 ? Math.max(1, ...totals) : 0;
  const yMax = Math.max(stackMax, totalsMax);
  const palette = seriesPalette(colors);
  const seriesColors = config.seriesColors;
  const barMaxWidth = 26;

  const stackSeries = config.series.map((s, si) => ({
    name: s.name,
    type: 'bar' as const,
    stack: 'total',
    data: s.values,
    barMaxWidth,
    z: 2,
    itemStyle: {
      color:
        seriesColors && seriesColors[si]
          ? seriesColors[si]
          : palette[si % palette.length],
    },
  }));

  const backgroundSeries =
    totals && totals.length > 0
      ? [
          {
            name: 'All reviews',
            type: 'bar' as const,
            data: totals,
            barMaxWidth,
            barGap: '-100%',
            z: 0,
            silent: false,
            itemStyle: {
              color: textColor,
              opacity: 0.16,
            },
            emphasis: { disabled: true },
          },
        ]
      : [];

  const legendCount =
    config.series.length + (backgroundSeries.length > 0 ? 1 : 0);
  const showZoom = config.showDataZoom === true;
  const zoom = config.zoom;

  const dataZoom = showZoom
    ? [
        {
          type: 'slider' as const,
          xAxisIndex: 0,
          height: 18,
          bottom: 28,
          borderColor: axisLineColor,
          fillerColor: 'rgba(128,128,128,0.25)',
          handleStyle: { color: textColor },
          textStyle: { color: textColor, fontSize: 10 },
          ...(zoom?.startValue !== undefined
            ? { startValue: zoom.startValue }
            : {}),
          ...(zoom?.endValue !== undefined ? { endValue: zoom.endValue } : {}),
        },
        {
          type: 'inside' as const,
          xAxisIndex: 0,
          // Keep page scroll usable over the chart; zoom via the slider instead.
          zoomOnMouseWheel: false,
          moveOnMouseWheel: false,
          ...(zoom?.startValue !== undefined
            ? { startValue: zoom.startValue }
            : {}),
          ...(zoom?.endValue !== undefined ? { endValue: zoom.endValue } : {}),
        },
      ]
    : undefined;

  return {
    ...base,
    title: chartTitle(config.title, textColor),
    tooltip: bodyTooltip({
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
    }),
    legend: multiSeriesLegend(legendCount, textColor),
    grid: cartesianGrid(
      config.title,
      legendCount,
      showZoom ? { bottom: legendCount > 1 ? 56 : 40 } : {}
    ),
    xAxis: {
      type: 'category',
      data: config.labels,
      axisLabel: { color: textColor, fontSize: 10 },
      axisLine: { lineStyle: { color: axisLineColor } },
    },
    yAxis: {
      type: 'value',
      max: yMax,
      axisLabel: { color: textColor, fontSize: 10 },
      splitLine: { lineStyle: { color: axisLineColor } },
    },
    ...(dataZoom ? { dataZoom } : {}),
    series: [...backgroundSeries, ...stackSeries],
  };
}
